import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  ClipboardList,
  Flag,
  Repeat2,
  Search,
  ShieldCheck,
  TrendingUp,
  Truck
} from 'lucide-react';
import TopBar from '../components/TopBar';
import PowerBIEmbed from '../components/PowerBIEmbed';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import '../modules/webv2/webV2Module.css';
import './COInstance.css';

const CO_INSTANCE_REPORT_ID = 'dfa75fa9-91da-4750-9e8b-df9da1be8b56';
const CO_INSTANCE_WORKSPACE_ID = '7d441575-4aa9-4260-99dd-543f002c0340';
const TA_TEAM_FILTER = 'vccm';

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'vccm-request'}-${index}`;

const coInstanceReportPages = [
  {
    key: 'critical-action-points',
    number: '1.',
    title: 'Critical Action Points',
    pageName: 'Critical Action Points',
    description: 'Priority stock risks across countries that require immediate attention.',
    color: 'red',
    icon: AlertTriangle
  },
  {
    key: 'kpis-risk-matrix',
    number: '2.',
    title: '🧩 KPIs and Risk matrix',
    pageName: '🧩 KPIs and Risk matrix',
    description: 'Executive KPI summary and risk matrix for vaccine stock monitoring.',
    color: 'teal',
    icon: BarChart3
  },
  {
    key: 'stock-visibility',
    number: '3.',
    title: 'Stock Visibility',
    pageName: '🔍  Stock Visibility',
    description: 'Country and supply-chain level visibility into reported stock positions.',
    color: 'blue',
    icon: Search
  },
  {
    key: 'reporting-consistency',
    number: '4.',
    title: 'Reporting Consistency',
    pageName: '🧮  Reporting Consistency',
    description: 'Consistency and reliability of stock reporting across supply-chain levels.',
    color: 'purple',
    icon: ClipboardList
  },
  {
    key: 'stock-sufficiency',
    number: '5.',
    title: 'Stock Sufficiency',
    pageName: '📋 Stock Sufficiency-Nationwide',
    description: 'Nationwide and supply-chain-level stock sufficiency views.',
    color: 'green',
    icon: ShieldCheck,
    children: [
      {
        key: 'stock-sufficiency-nationwide',
        number: '5a.',
        title: 'Nationwide',
        pageName: 'Stock Sufficiency-Nationwide',
        icon: ShieldCheck
      },
      {
        key: 'stock-sufficiency-cs',
        number: '5b.',
        title: 'Central Store',
        pageName: '📋  Stock Sufficiency-CS',
        icon: ShieldCheck
      },
      {
        key: 'stock-sufficiency-sn',
        number: '5c.',
        title: 'Subnational Stores',
        pageName: '📋  Stock Sufficiency-SN',
        icon: ShieldCheck
      },
      {
        key: 'stock-sufficiency-ld',
        number: '5d.',
        title: 'District / Last Mile',
        pageName: '📋  Stock Sufficiency-LD',
        icon: ShieldCheck
      }
    ]
  },
  {
    key: 'pipelines',
    number: '6.',
    title: 'Pipelines',
    pageName: '🚚 Pipelines',
    description: 'Pipeline supply status and incoming shipment views.',
    color: 'blue',
    icon: Truck
  },
  {
    key: 'stock-projections',
    number: '7.',
    title: 'Stock Projections',
    pageName: '📝  Stock Projections',
    description: 'Projected stock status and forward-looking coverage signals.',
    color: 'purple',
    icon: TrendingUp
  },
  {
    key: 'stockouts',
    number: '8.',
    title: 'Stockouts',
    pageName: '🚨  Stockouts heatmap',
    description: 'Stockout heatmaps, trends and reported causes.',
    color: 'red',
    icon: AlertTriangle,
    children: [
      {
        key: 'stockouts-heatmap',
        number: '8a.',
        title: 'Heatmap',
        pageName: '🚨  Stockouts heatmap',
        icon: Flag
      },
      {
        key: 'stockout-trends',
        number: '8b.',
        title: 'Trends',
        pageName: '📊 Stockout trends',
        icon: TrendingUp
      },
      {
        key: 'causes-of-stockouts',
        number: '8c.',
        title: 'Causes',
        pageName: '💡Causes of stockouts',
        icon: Repeat2
      }
    ]
  }
];

const coInstanceReportPageMap = new Map(
  coInstanceReportPages.flatMap((page) => [
    [page.key, page],
    ...(page.children || []).map((child) => [child.key, { ...child, parentColor: page.color }])
  ])
);

const COInstance = ({ userClaims, isEmbedded = false }) => {
  const [activeReportPageKey, setActiveReportPageKey] = useState(coInstanceReportPages[0].key);
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});
  const activePage = coInstanceReportPageMap.get(activeReportPageKey) || coInstanceReportPages[0];

  useEffect(() => {
    if (!isEmbedded) {
      setIsTaLoading(false);
      return undefined;
    }

    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const datasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
        const workspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
        const daxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
        const tableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';
        const effectiveQuery = String(daxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(tableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(datasetId, workspaceId, effectiveQuery, 5000, effectiveTableName);

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [isEmbedded]);

  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, ['request title', 'request', 'title', 'activity', 'intervention', 'action', 'issue']),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const vccmTaRequests = useMemo(() => (
    taRows.filter((row) => getRowText(row, taLeadCoeTeamColumnKey).toLowerCase().includes(TA_TEAM_FILTER))
  ), [taRows, taLeadCoeTeamColumnKey]);

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  const content = (
    <section className="home-section vaccines-page-section vaccines-dashboard thrive-stock-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <h2 className="vaccines-dashboard-title">Vaccine and Cold Chain Country Instance</h2>
        </div>
      </div>

      <div className="thrive-stock-nav-card">
        <div className="thrive-stock-nav-header">
          <span className="vaccines-section-label">Report Pages</span>
          <strong>{activePage.number} {activePage.title}</strong>
          <p>{activePage.description}</p>
        </div>
        <nav className="thrive-stock-menu-bar" aria-label="Country instance Power BI report pages">
          {coInstanceReportPages.map((page) => {
            const Icon = page.icon;
            const isParentActive = activePage.key === page.key || page.children?.some((child) => child.key === activePage.key);

            return (
              <div key={page.key} className="thrive-stock-menu-item">
                <button
                  type="button"
                  className={`thrive-stock-menu-button is-${page.color} ${isParentActive ? 'is-active' : ''}`}
                  onClick={() => setActiveReportPageKey(page.key)}
                >
                  <Icon size={16} aria-hidden="true" />
                  <span>{page.number} {page.title}</span>
                </button>
                {page.children?.length > 0 && (
                  <div className="thrive-stock-submenu" role="menu">
                    {page.children.map((child) => {
                      const ChildIcon = child.icon;

                      return (
                        <button
                          key={child.key}
                          type="button"
                          role="menuitem"
                          className={`thrive-stock-submenu-button ${activePage.key === child.key ? 'is-active' : ''}`}
                          onClick={() => setActiveReportPageKey(child.key)}
                        >
                          <ChildIcon size={15} aria-hidden="true" />
                          <span>{child.number} {child.title}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className="thrive-stock-report-card">
        <div className="global-coverage-embed co-instance-report-embed">
          <PowerBIEmbed
            reportId={CO_INSTANCE_REPORT_ID}
            workspaceId={CO_INSTANCE_WORKSPACE_ID}
            pageName={activePage.pageName}
            height="100%"
          />
        </div>
      </div>

      {isEmbedded && (
      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">VCCM TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : `${vccmTaRequests.length} request${vccmTaRequests.length === 1 ? '' : 's'} for VCCM`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="VCCM TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading VCCM TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && vccmTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No VCCM TA requests found.</td>
                </tr>
              )}
              {!isTaLoading && vccmTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey) || getRowText(row, taTitleColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || 'VCCM'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </section>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="co-instance" userClaims={userClaims} />
      <div className="co-instance-native-compat v2-module">
        {content}
      </div>
    </div>
  );
};

export default COInstance;



